import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Swords,
  Bot,
  Zap,
  ShieldAlert,
  Loader2,
  X,
  Award,
  AlertCircle,
  ChevronRight,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import {
  joinQueue,
  leaveQueue,
  typedOn,
  typedOff,
} from '@fog-chess/core';
import { useMatchmakingStore, calculateGateStatus } from '@fog-chess/core';
import { getMyRank } from '@fog-chess/core';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { GAME_SOCKET_EVENTS } from '@/lib/socket';

/**
 * QuickMatchScreen - Mandate D (Proactive Visual Gate Enforcement).
 *
 * Gate thresholds (backend enforced, UI mirrors proactively):
 *   - PvP Quick Match:     requires score >= 200
 *   - AI Hard:             requires score >= 700
 *   - AI Medium / Easy:    always available
 *
 * Flow:
 *  1. Fetch user's live score/rank via TanStack Query (getMyRank).
 *  2. Compute gate status locally (calculateGateStatus) for instant feedback.
 *  3. Render mode cards; locked ones show lock icon + "Needs X more points" badge.
 *  4. On click of locked card -> open styled modal explaining how to earn points.
 *  5. On click of unlocked card -> emit queue:join, show animated radar + timer.
 *  6. Cancel Search -> emit queue:leave, clear matchmaking store.
 *  7. GlobalMatchmakingListener (root) auto-navigates on match:found / match:started.
 *  8. error:gate_locked socket handler as secondary safety net (toast + reset).
 */

const GATE_THRESHOLDS = {
  PVP: 200,
  HARD_AI: 700,
} as const;

type Mode = 'pvp' | 'easy' | 'medium' | 'hard';

interface ModeConfig {
  id: Mode;
  label: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
  /** required score; null = no gate */
  gateScore: number | null;
  /** human-readable gate text */
  gateText: string;
}

const MODES: ModeConfig[] = [
  {
    id: 'pvp',
    label: 'PvP Quick Match',
    description: 'Matched by your FTPES stage. Real-time fog of war.',
    icon: (
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500/15 text-sky-500">
        <Swords className="h-5 w-5" />
      </div>
    ),
    accent: 'from-sky-500/20 to-sky-500/0',
    gateScore: GATE_THRESHOLDS.PVP,
    gateText: 'Needs 200+ FTPES',
  },
  {
    id: 'easy',
    label: 'AI — Easy',
    description: 'Fog Engine (≈400 FTPES). Great for learning the fog.',
    icon: (
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-500">
        <Bot className="h-5 w-5" />
      </div>
    ),
    accent: 'from-emerald-500/20 to-emerald-500/0',
    gateScore: null,
    gateText: 'Always available',
  },
  {
    id: 'medium',
    label: 'AI — Medium',
    description: 'Fog Engine (≈900 FTPES). Balanced challenge.',
    icon: (
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15 text-amber-500">
        <Zap className="h-5 w-5" />
      </div>
    ),
    accent: 'from-amber-500/20 to-amber-500/0',
    gateScore: null,
    gateText: 'Always available',
  },
  {
    id: 'hard',
    label: 'AI — Hard',
    description: 'Fog Engine (≈1800 FTPES). Ruthless precision.',
    icon: (
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-500/15 text-rose-500">
        <ShieldAlert className="h-5 w-5" />
      </div>
    ),
    accent: 'from-rose-500/20 to-rose-500/0',
    gateScore: GATE_THRESHOLDS.HARD_AI,
    gateText: 'Needs 700+ FTPES',
  },
];

function ModeCard({
  mode,
  locked,
  pointsNeeded,
  onSelect,
  selected,
}: {
  mode: ModeConfig;
  locked: boolean;
  pointsNeeded: number;
  onSelect: () => void;
  selected: boolean;
}) {
  return (
    <motion.button
      onClick={onSelect}
      disabled={locked}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.05 }}
      className={cn(
        'relative w-full text-left p-5 rounded-xl border-2 transition-all',
        locked
          ? 'border-border/60 bg-muted/40 cursor-not-allowed opacity-70'
          : 'border-border hover:border-primary/50 bg-background',
        selected && !locked && 'border-primary bg-primary/5',
      )}
    >
      {locked && (
        <div className="pointer-events-none absolute inset-0 rounded-xl bg-black/10" />
      )}
      <div className="relative flex items-start gap-4">
        <span
          className={cn(
            'flex-shrink-0',
            locked && 'opacity-40 grayscale',
          )}
        >
          {mode.icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={cn('font-semibold', locked && 'text-muted-foreground')}>
              {mode.label}
            </h3>
            {locked && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="fog" className="gap-1">
                      <AlertCircle className="h-3 w-3 flex-shrink-0" />
                      Locked
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center">
                    <div className="text-center">
                      <p className="font-medium">
                        Requires {mode.gateScore} FTPES
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {pointsNeeded} more points needed
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
            {mode.description}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Badge variant={locked ? 'fog' : 'default'} className="gap-1">
              {mode.gateText}
            </Badge>
            {selected && !locked && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-1 text-sm font-medium text-primary"
              >
                <ChevronRight className="h-3.5 w-3.5" />
                Selected
              </motion.span>
            )}
          </div>
        </div>
      </div>
      {locked && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive"
        >
          <AlertCircle className="h-3 w-3" />
          <span>+{pointsNeeded} pts</span>
        </motion.div>
      )}
    </motion.button>
  );
}

function LockedGateModal({
  mode,
  userScore,
  pointsNeeded,
  onClose,
  onEarnPoints,
}: {
  mode: ModeConfig;
  userScore: number;
  pointsNeeded: number;
  onClose: () => void;
  onEarnPoints: () => void;
}) {
  const tips = {
    pvp: [
      'Win matches against Easy AI (+20 FTPES per win at Stage 1).',
      'Win matches against Medium AI (+15 FTPES per win at Stage 2).',
      'Each win against AI builds your FTPES toward the 200 gate.',
    ],
    hard: [
      'Reach Stage 2 (500 FTPES) by beating Medium AI consistently.',
      'At Stage 2, Medium wins grant +15 FTPES; climb to Stage 3 (1000).',
      'Stage 3+ unlocks Hard AI naturally — no shortcut needed.',
    ],
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            {mode.label} is locked
          </DialogTitle>
          <DialogDescription>
            You need <strong>{pointsNeeded} more FTPES</strong> to unlock this mode.
            Your current score: <strong>{userScore} FTPES</strong>.
          </DialogDescription>
        </DialogHeader>
        <Separator className="my-4" />
        <div className="space-y-3">
          <p className="text-sm font-medium">How to earn FTPES:</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {tips[mode.id === 'hard' ? 'hard' : 'pvp'].map((tip, i) => (
              <li key={i} className="flex gap-2">
                <span className="flex-shrink-0 mt-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Not now
          </Button>
          <Button onClick={onEarnPoints} className="flex-1">
            <TrendingUp className="mr-2 h-4 w-4" />
            Start earning
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Animated radar pulse + timer while queued. */
function QueueRadar({ secondsQueued, onCancel }: { secondsQueued: number; onCancel: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="w-full max-w-md mx-auto space-y-5"
    >
      {/* Radar rings */}
      <div className="relative h-48 w-48 mx-auto">
        <motion.div
          animate={{ scale: [1, 2], opacity: [0.6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
          className="absolute inset-0 rounded-full border-2 border-primary/40"
        />
        <motion.div
          animate={{ scale: [1, 2], opacity: [0.4, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: 0.6 }}
          className="absolute inset-0 rounded-full border-2 border-primary/30"
        />
        <motion.div
          animate={{ scale: [1, 2], opacity: [0.2, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: 1.2 }}
          className="absolute inset-0 rounded-full border-2 border-primary/20"
        />
        {/* Center pulse */}
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [1, 0.7, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Swords className="h-14 w-14 text-primary/60" />
        </motion.div>
      </div>

      <div className="text-center">
        <motion.h3
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xl font-semibold"
        >
          Searching for opponent…
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-1 text-sm text-muted-foreground font-mono"
        >
          Queued for {secondsQueued}s
        </motion.p>
      </div>

      <Button
        variant="destructive"
        className="w-full"
        onClick={onCancel}
        size="lg"
      >
        <X className="mr-2 h-4 w-4" />
        Cancel Search
      </Button>
    </motion.div>
  );
}

/** Fetches user rank/score for proactive gating. */
function useUserScore() {
  return useQuery({
    queryKey: ['my-rank'],
    queryFn: getMyRank,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}

export function QuickMatchScreen() {
  const navigate = useNavigate();
  const clearMatchFound = useMatchmakingStore((s) => s.clearMatchFound);

  // Gate state from matchmaking store (calculated locally via calculateGateStatus)
  const [selectedMode, setSelectedMode] = useState<Mode | null>(null);
  const [showGateModal, setShowGateModal] = useState<Mode | null>(null);
  const [queuedMode, setQueuedMode] = useState<Mode | null>(null);

  // Fetch live score
  const { data: rankData, isLoading: loadingScore } = useUserScore();
  const score = rankData?.score ?? 0;
  const gateStatus = calculateGateStatus(score);

  // Sync gate status into matchmaking store so other screens/components see it
  useEffect(() => {
    useMatchmakingStore.getState().setGateStatus(
      gateStatus.pvpLocked,
      gateStatus.pvpPointsNeeded,
      gateStatus.hardAiLocked,
      gateStatus.hardAiPointsNeeded,
      score,
      gateStatus.stage,
    );
  }, [score, gateStatus]);

  // Socket listener for error:gate_locked (secondary safety net)
  useEffect(() => {
    const handler = (...args: unknown[]) => {
      const payload = args[0] as { reason: string; details?: Record<string, unknown> } | undefined;
      if (!payload) return;
      if (payload.details?.gate === 'pvp' || payload.details?.gate === 'hard_ai') {
        toast.error(`Gate locked: ${payload.reason}`);
      } else {
        toast.error(payload.reason || 'Matchmaking gate error');
      }
      // Clear local queue state so UI returns to mode selection
      setQueuedMode(null);
      selectedMode && setSelectedMode(null);
    };

    typedOn(GAME_SOCKET_EVENTS.ERROR_GATE_LOCKED, handler);
    return () => typedOff(GAME_SOCKET_EVENTS.ERROR_GATE_LOCKED, handler);
  }, [selectedMode]);

  const handleModeClick = (modeId: Mode) => {
    const mode = MODES.find((m) => m.id === modeId)!;
    const locked = mode.gateScore !== null && score < mode.gateScore;

    if (locked) {
      setShowGateModal(modeId);
      return;
    }

    // Unlocked: join queue
    setSelectedMode(modeId);
    setQueuedMode(modeId);
    joinQueue(); // emits queue:join
  };

  const handleCancelSearch = () => {
    leaveQueue(); // emits queue:leave
    setQueuedMode(null);
    setSelectedMode(null);
    clearMatchFound();
  };

  const handleEarnPoints = (modeId: Mode) => {
    if (modeId === 'pvp') {
      navigate('/quick-match');
      setSelectedMode('easy');
    } else {
      navigate('/quick-match');
      setSelectedMode('medium');
    }
    setShowGateModal(null);
  };

  // Calculate queue duration
  const [queueDuration, setQueueDuration] = useState(0);
  useEffect(() => {
    if (!queuedMode) {
      setQueueDuration(0);
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      setQueueDuration(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [queuedMode]);

  if (loadingScore && score === 0) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Quick Match</h1>
            <p className="text-muted-foreground">
              Choose your opponent. Gates unlock as your FTPES grows.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="default" className="gap-1.5">
              <Award className="h-3.5 w-3.5" />
              <span>{score} FTPES</span>
            </Badge>
            <Badge variant="fog" className="gap-1.5">
              <Award className="h-3.5 w-3.5" />
              Stage {gateStatus.stage}
            </Badge>
          </div>
        </motion.div>

        {/* Mode Grid */}
        <AnimatePresence mode="wait">
          {!queuedMode ? (
            <motion.div
              key="modes"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="grid gap-4 sm:grid-cols-2"
            >
              {MODES.map((mode) => (
                <ModeCard
                  key={mode.id}
                  mode={mode}
                  locked={mode.gateScore !== null && score < mode.gateScore}
                  pointsNeeded={mode.gateScore ? Math.max(0, mode.gateScore - score) : 0}
                  onSelect={() => handleModeClick(mode.id)}
                  selected={selectedMode === mode.id}
                />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="queue"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              className="max-w-md mx-auto"
            >
              <QueueRadar secondsQueued={queueDuration} onCancel={handleCancelSearch} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gate locked modal */}
        <AnimatePresence>
          {showGateModal && (
            <LockedGateModal
              mode={MODES.find((m) => m.id === showGateModal)!}
              userScore={score}
              pointsNeeded={
                MODES.find((m) => m.id === showGateModal)!.gateScore!
                ? Math.max(0, MODES.find((m) => m.id === showGateModal)!.gateScore! - score)
                : 0
              }
              onClose={() => setShowGateModal(null)}
              onEarnPoints={() => handleEarnPoints(showGateModal!)}
            />
          )}
        </AnimatePresence>
      </div>
    </TooltipProvider>
  );
}